import React, { useEffect } from 'react';

// CheckoutDrawer — the reusable right-side slide-over shell for transactional
// flows (the "drawer-first" cornerstone). Desktop: a fixed ~460px panel over a
// scrim; mobile: a full-screen sheet. Generic by design — it owns only the
// shell (scrim, slide animation, header, scrollable body, sticky footer) and
// renders whatever checkout content the caller passes as children. The same
// shell is reused by the FINISHED-state checkout, the standalone register, and
// the dealer deliver-&-invoice flow.
//
// Props:
//   show      bool          — open/closed (kept mounted for the slide transition)
//   onHide    () => void    — scrim click / close button / Esc
//   title     node|string   — header title
//   subtitle  node|string   — optional secondary header line
//   footer    node          — optional sticky footer (actions)
//   width     number        — desktop panel width (default 460)
//   children  node          — body content

const CheckoutDrawer = ({ show, onHide, title, subtitle, footer, width = 460, children }) => {
  // Lock background scroll while open.
  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [show]);

  // Esc to close.
  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && onHide) onHide(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, onHide]);

  return (
    <div className={`jv-drawer-root ${show ? 'open' : ''}`} aria-hidden={!show}>
      <div className="jv-drawer-scrim" onClick={onHide} />
      <div className="jv-drawer-panel" style={{ width }} role="dialog" aria-modal="true">
        <div className="jv-drawer-header">
          <div className="jv-drawer-titles">
            <div className="jv-drawer-title">{title}</div>
            {subtitle ? <div className="jv-drawer-subtitle">{subtitle}</div> : null}
          </div>
          <button type="button" className="jv-drawer-close" onClick={onHide} aria-label="Close">
            <i className="fa-sharp fa-regular fa-xmark" />
          </button>
        </div>

        <div className="jv-drawer-body">{children}</div>

        {footer ? <div className="jv-drawer-footer">{footer}</div> : null}
      </div>

      <style>{`
        .jv-drawer-root {
          position: fixed;
          inset: 0;
          /* Below Bootstrap's modal backdrop (1040) so PaymentProgressModal /
             StripeRecoveryModal surface above the drawer during card/terminal. */
          z-index: 1035;
          pointer-events: none;
        }
        .jv-drawer-root.open { pointer-events: auto; }
        .jv-drawer-scrim {
          position: absolute;
          inset: 0;
          background: rgba(31, 41, 55, 0.55);
          opacity: 0;
          transition: opacity .25s ease;
        }
        .jv-drawer-root.open .jv-drawer-scrim { opacity: 1; }
        .jv-drawer-panel {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          max-width: 100vw;
          background: #fff;
          box-shadow: -8px 0 24px rgba(31, 41, 55, 0.15);
          border-top-left-radius: 12px;
          border-bottom-left-radius: 12px;
          display: flex;
          flex-direction: column;
          transform: translateX(100%);
          transition: transform .25s ease;
        }
        .jv-drawer-root.open .jv-drawer-panel { transform: translateX(0); }
        .jv-drawer-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          padding: 20px 24px;
          border-bottom: 1px solid #E5E7EB;
        }
        .jv-drawer-titles { text-align: left; }
        .jv-drawer-title { font-size: 20px; font-weight: 700; color: #1F2937; text-align: left; }
        .jv-drawer-subtitle { font-size: 13px; color: #6B7280; margin-top: 4px; text-align: left; }
        .jv-drawer-close {
          background: none; border: none; cursor: pointer;
          color: #6B7280; font-size: 20px; line-height: 1; padding: 2px 6px;
        }
        .jv-drawer-body { flex: 1; overflow-y: auto; padding: 20px 24px; }
        .jv-drawer-footer { padding: 16px 24px; border-top: 1px solid #E5E7EB; }
        @media (max-width: 520px) {
          .jv-drawer-panel {
            width: 100% !important;
            border-radius: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CheckoutDrawer;
